import memoize from 'memoize-one';
import deepDiff from 'deep-diff';

import { UnicornApi, Analytics, EnvironmentData } from '@cld/unicorn-api';
import { createUnicornApi } from '../domain/createUnicornApi';
import { DispatchAction } from '../actions';
import { SubscribeToStateChanges, UnsubscribeFromStateChanges } from '../middleware/subscriptionMiddleware';
import { State } from '../state';
import { ExternalLoggerFactory } from '../../api/createExternalLoggerFactory';
import { AbTestingClientState, CloudinaryFetchClient, ConsoleLogger } from '../types';

const memoizedCreateUnicornApi = memoize(
  (_: ConsoleLogger, ...params: Parameters<typeof createUnicornApi>) => createUnicornApi(...params),
  ([_, oldApiParameters], [consoleLogger, newApiParameters]) => {
    const difference = deepDiff(oldApiParameters, newApiParameters);
    const isEqual = (difference?.length ?? 0) === 0;

    if (!isEqual) {
      consoleLogger.debug('general', 'Unicorn API change triggered', { difference });
    }

    return isEqual;
  }
);

export type UnicornApiState = ReturnType<typeof selectUnicornApi>;

export const selectUnicornApi = ({
  analytics,
  abTestingClientState,
  dispatch,
  environmentData,
  consoleLogger,
  subscribeToStateChanges,
  unsubscribeFromStateChanges,
  externalLoggerFactory,
  createCloudinaryFetchClient,
  state,
}: {
  analytics: Analytics;
  abTestingClientState: AbTestingClientState;
  dispatch: DispatchAction;
  environmentData: EnvironmentData;
  subscribeToStateChanges: SubscribeToStateChanges;
  unsubscribeFromStateChanges: UnsubscribeFromStateChanges;
  createCloudinaryFetchClient: UnicornApi['methods']['createFetchClient'];
  consoleLogger: ConsoleLogger;
  externalLoggerFactory: ExternalLoggerFactory;
  state: State;
  // FIXME: "error" state is not needed anymore after state adjustments, but removing needs consumer changes
}): { status: 'loading' } | { status: 'ready'; api: UnicornApi } | { status: 'error' } => {
  if (state.applicationStatus !== 'ready' || abTestingClientState.status === 'loading') {
    return { status: 'loading' };
  }

  return {
    status: 'ready',
    api: memoizedCreateUnicornApi(consoleLogger, {
      analytics,
      abTestingClient: abTestingClientState.client,
      dispatch,
      environmentData,
      consoleLogger,
      externalLoggerFactory,
      createCloudinaryFetchClient,
      subscribeToStateChanges,
      unsubscribeFromStateChanges,
      account: state.account,
      capabilities: state.capabilities,
      permissions: state.permissions,
      productEnvironment: state.productEnvironment,
      deprecatedData: state.deprecatedData,
      user: state.user,
      plan: state.plan,
      assetVisibility: state.assetVisibility,
      theme: state.theme,
      impersonationActive: state.isImpersonationActive,
    }),
  };
};
